
import React from "react";
import { useTranslation } from "react-i18next";

interface PointDisplayProps {
  currentPoint: string;
  currentAttempt: number;
  maxAttempts: number;
  distance: string;
  isChippingTest: boolean;
  getFormattedPoint: (point: string) => string;
  currentLevel?: 1 | 2 | 3;
}

const PointDisplay: React.FC<PointDisplayProps> = ({
  currentPoint,
  currentAttempt,
  maxAttempts,
  distance,
  isChippingTest,
  getFormattedPoint,
  currentLevel = 2, // Level 2 by default
}) => {
  const { t } = useTranslation("training");
  
  // Determine if this is a wedging test or iron test
  const pathname = window.location.pathname;
  const isWedgingTest = pathname.includes('wedging');
  const isIronTest = pathname.includes('iron') || (pathname.includes('level2') && currentPoint === 'D' && !isChippingTest && !isWedgingTest);
  const isFairwayWoodTest = pathname.includes('fairwayWood');
  const isDriverTest = pathname.includes('driver');
  
  // Determine the displayed distance specifically for level 2
  const getDistanceByLevel = () => {
    // If a distance is explicitly provided, use it
    if (distance) {
      // Add "m" unit if it's a number without unit
      if (!isNaN(Number(distance.replace(',', '.').replace('m', ''))) && !distance.includes('m')) {
        return `${distance}m`;
      }
      return distance;
    }
    
    // Tests de fer spécifiques au Niveau 2
    if ((isIronTest || currentPoint === 'D') && pathname.includes('level2')) {
      if (pathname.includes('iron-long')) return '130m';
      if (pathname.includes('iron-mid')) return '80m';
    }
    
    // Tests de bois de parcours spécifiques au Niveau 2
    if ((isFairwayWoodTest || currentPoint === 'D') && pathname.includes('level2')) {
      if (pathname.includes('fairwayWood-3')) return '180m';
      if (pathname.includes('fairwayWood-5')) return '160m';
    }
    
    // Tests de driver spécifiques au Niveau 2
    if ((isDriverTest || currentPoint === 'D') && pathname.includes('level2')) {
      if (pathname.includes('driver')) return '180m';
    }
    
    // Wedging specific distances for Level 2
    if (isWedgingTest && pathname.includes('level2')) {
      if (currentPoint === 'D') return '45m';
      if (currentPoint === 'E') return '55m';
      if (currentPoint === 'F') return '65m';
      if (currentPoint === 'D1') return '75m';
      if (currentPoint === 'E1') return '85m';
      if (currentPoint === 'F1') return '95m';
      if (currentPoint === 'D2') return '45m';
      if (currentPoint === 'E2') return '55m';
      if (currentPoint === 'F2') return '65m';
    }
    
    // Level 2 distances - Chipping specific
    if (isChippingTest && pathname.includes('level2')) {
      if (currentPoint === 'D') return '15m';
      if (currentPoint === 'E') return '20m';
      if (currentPoint === 'F') return '25m';
      if (currentPoint === 'D1') return '15m';
      if (currentPoint === 'E1') return '20m';
      if (currentPoint === 'F1') return '25m';
      if (currentPoint === 'D2') return '15m';
      if (currentPoint === 'E2') return '20m';
      if (currentPoint === 'F2') return '25m';
      if (currentPoint === 'D3') return '5m';
      if (currentPoint === 'E3') return '10m';
      if (currentPoint === 'F3') return '15m';
    } else if (pathname.includes('level2')) {
      // Standard Level 2 putting distances
      if (currentPoint === 'D') return '1,2m';
      if (currentPoint === 'E') return '1,5m';
      if (currentPoint === 'F') return '3m';
      if (currentPoint === 'D1') return '1,2m';
      if (currentPoint === 'E1') return '1,5m';
      if (currentPoint === 'F1') return '3m';
      if (currentPoint === 'D2') return '1,2m';
      if (currentPoint === 'E2') return '1,5m';
      if (currentPoint === 'F2') return '3m';
      if (currentPoint === 'D3') return '1,2m';
      if (currentPoint === 'E3') return '1,5m';
      if (currentPoint === 'F3') return '3m';
      if (currentPoint === 'D4') return '1,2m';
      if (currentPoint === 'E4') return '1,5m';
      if (currentPoint === 'F4') return '3m';
    }
    
    // Level 3 wedging points
    if (currentPoint.startsWith('Wedging')) {
      if (currentPoint === 'WedgingA') return '30m';
      if (currentPoint === 'WedgingB') return '40m';
      if (currentPoint === 'WedgingC') return '50m';
      if (currentPoint === 'WedgingD') return '60m';
      if (currentPoint === 'WedgingE') return '70m';
      if (currentPoint === 'WedgingF') return '90m';
    }
    
    // Level 3 iron points
    if (currentPoint.startsWith('Iron')) {
      if (currentPoint === 'IronA') return '110m';
      if (currentPoint === 'IronB') return '130m';
      if (currentPoint === 'IronC') return '150m';
    }
    
    // Default: use the formatted point
    return getFormattedPoint(currentPoint);
  };
  
  const displayDistance = getDistanceByLevel();

  return (
    <div className="text-center mb-4">
      <h3 className="text-xl font-semibold mb-1">
        {t("interface.currentPoint")}: {currentPoint} - {t("interface.attempt")} {currentAttempt + 1}/{maxAttempts}
      </h3>
      <p className="text-green-600 font-medium">
        {t("interface.distance")}: {displayDistance}
      </p>
    </div>
  );
};

export default PointDisplay;
